import { useState, useEffect } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  FileText,
  DollarSign,
  CheckCircle2,
  Clock,
  Search,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const statusStyles = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-200",
  posted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  partial: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  void: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500",
};

export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/client-portal/invoices`);
        if (res.ok) {
          const data = await res.json();
          setInvoices(data.invoices || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchInvoices();
  }, []);

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/invoices/${selectedInvoice.invoice_id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: paymentMethod, notes: paymentNotes }),
      });
      if (res.ok) {
        toast.success('Payment reported! Your accountant will confirm shortly.');
        setPayDialogOpen(false);
        setSelectedInvoice(null);
        setPaymentNotes('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to report payment');
      }
    } catch (e) { toast.error('Failed to report payment'); }
    finally { setSubmitting(false); }
  };

  const filtered = invoices.filter(inv => {
    const matchesSearch = !search || 
      String(inv.invoice_no).includes(search) ||
      inv.invoice_id.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-invoices">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Your Invoices</h1>
        <p className="text-muted-foreground text-sm mt-1">View and manage your invoices. Report payments or pay online.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by invoice #..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="invoice-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="invoice-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="posted">Outstanding</SelectItem>
            <SelectItem value="partial">Partially Paid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Balance Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length > 0 ? filtered.map(inv => (
                <TableRow key={inv.invoice_id}>
                  <TableCell className="font-medium">#{inv.invoice_no}</TableCell>
                  <TableCell className="text-sm">{inv.invoice_date}</TableCell>
                  <TableCell className="text-sm">{inv.due_date}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrency(inv.total)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrency(inv.balance_due)}</TableCell>
                  <TableCell>
                    <Badge className={cn("text-[10px] capitalize", statusStyles[inv.status] || statusStyles.draft)}>
                      {inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.status !== 'paid' && inv.status !== 'void' && inv.status !== 'draft' && (
                      <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        {inv.payment_token && (
                          <Button
                            size="sm"
                            onClick={() => {
                              // Open the public pay page in a new tab so the
                              // user keeps their portal session alive.
                              window.open(`/pay/${inv.payment_token}`, '_blank', 'noopener,noreferrer');
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`pay-online-${inv.invoice_no}`}
                          >
                            <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                            Pay Online
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedInvoice(inv); setPayDialogOpen(true); }}
                          data-testid={`pay-invoice-${inv.invoice_no}`}
                        >
                          Report Payment
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No invoices found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Report Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Payment</DialogTitle>
            <DialogDescription>
              Report a payment for Invoice #{selectedInvoice?.invoice_no}. Your accountant will confirm the payment.
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Amount Due</span>
                <span className="text-lg font-bold">{formatCurrency(selectedInvoice.balance_due)}</span>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer / ACH</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="e.g., Check #1234, transferred on Jan 15..."
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                  data-testid="payment-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={submitting} data-testid="submit-payment-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Report Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
